
import React, { useState } from 'react';
import { Box, Button, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface AssistantFooterProps {
  activeStep: number;
  totalSteps: number;
  onBack: () => void;
  onNext: () => void;
  isNextDisabled: boolean;
  isSubmitting?: boolean;
}

const AssistantFooter: React.FC<AssistantFooterProps> = ({
  activeStep,
  totalSteps,
  onBack,
  onNext,
  isNextDisabled,
  isSubmitting = false,
}) => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Create a reference to the handleSubmit function in the DeviceStep component
  const deviceStepSubmitRef = React.useRef<() => void>(() => {});

  // Register the submit function when it's provided by DeviceStep
  React.useEffect(() => {
    // @ts-ignore - Custom property added to window
    window.registerDeviceStepSubmit = (submitFn: () => void) => {
      deviceStepSubmitRef.current = submitFn;
    };

    return () => {
      // @ts-ignore - Remove the property when the component unmounts
      delete window.registerDeviceStepSubmit;
    };
  }, []);

  const handleNextClick = async () => {
    // For step 4 (Device Step), call the registered submit function
    if (activeStep === 4) {
      setIsProcessing(true);
      try {
        await deviceStepSubmitRef.current();
      } finally {
        setIsProcessing(false);
      }
    } else {
      onNext();
    }
  };

  return (
    <Box sx={{
      p: 2,
      display: 'flex',
      justifyContent: 'space-between',
      borderTop: '1px solid',
      borderColor: 'divider',
      position: isMobile ? 'sticky' : 'static',
      bottom: 0,
      zIndex: 1,
      bgcolor: 'background.default',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <Button
        color="inherit"
        onClick={onBack}
        sx={{ mr: 1 }}
        disabled={activeStep === 0 || isProcessing || isSubmitting}
      >
        {t('assistant.buttons.back')}
      </Button>
      <Box sx={{ flex: '1 1 auto' }} />
      {activeStep === totalSteps - 1 ? (
        <Button
          onClick={handleNextClick}
          variant="contained"
          color="primary"
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isSubmitting ? t('assistant.reviewStep.submitting') : t('assistant.buttons.finish')}
        </Button>
      ) : (
        <Button
          variant="contained"
          color="primary"
          onClick={handleNextClick}
          disabled={isNextDisabled || isProcessing}
          startIcon={isProcessing && activeStep === 4 ? <CircularProgress size={20} color="inherit" /> : null}
        >
          {isProcessing && activeStep === 4
            ? t('assistant.buttons.processing')
            : t('assistant.buttons.next')}
        </Button>
      )}
    </Box>
  );
};

export default AssistantFooter;
